import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Filter } from "lucide-react";
import { api } from "../lib/api";
import { usePageDescription } from "../lib/usePageDescription";

const Rares = () => {
  const [rares, setRares] = useState([]);
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("Alle");
  const pd = usePageDescription("rares", { title: "Seltene Möbel & Rares", intro: "Die wertvollsten Habbo-Möbel der deutschsprachigen Hotel-Geschichte – kategorisiert nach Typ." });

  useEffect(() => {
    (async () => {
      try {
        const [rs, cs] = await Promise.all([api.listRares(), api.listRareCategories()]);
        setRares(rs);
        setCats(cs);
      } finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(() => {
    if (filter === "Alle") return rares;
    return rares.filter((r) => (r.category || "Sammler") === filter);
  }, [filter, rares]);

  return (
    <section className="mx-auto max-w-7xl px-4 py-10">
      <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-[#9d7bff]">Sammlerstücke</div>
      <h1 className="mt-1 text-3xl font-bold text-white" data-testid="page-title">{pd.title}</h1>
      <p className="mt-2 text-sm text-zinc-400 max-w-2xl" data-testid="page-intro">{pd.intro}</p>

      <div className="mt-6 flex flex-wrap gap-2">
        <button
          onClick={() => setFilter("Alle")}
          className={`rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${
            filter === "Alle"
              ? "border-[#7c4dff]/60 bg-[#7c4dff]/20 text-white"
              : "border-white/10 bg-white/5 text-zinc-300 hover:border-white/20"
          }`}
        >
          <Filter className="mr-1 inline h-3 w-3" /> Alle ({rares.length})
        </button>
        {cats.map((c) => (
          <button
            key={c.name}
            onClick={() => setFilter(c.name)}
            className={`rounded-full px-3 py-1.5 text-xs font-medium border transition-colors ${
              filter === c.name
                ? "border-[#7c4dff]/60 bg-[#7c4dff]/20 text-white"
                : "border-white/10 bg-white/5 text-zinc-300 hover:border-white/20"
            }`}
          >
            {c.name} ({c.count})
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20 text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
      ) : (
        <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          {filtered.map((r) => (
            <div key={r.id} className="card-hover rounded-xl border border-white/10 bg-[#0f141b] p-4 text-center">
              <div className="mx-auto flex h-24 w-24 items-center justify-center rounded-xl bg-[#10141b] border border-white/5 overflow-hidden">
                {r.image ? (
                  <img src={r.image} alt={r.name} className="hb-pixel-img max-h-20 max-w-20" />
                ) : null}
              </div>
              <div className="mt-3 inline-block rounded bg-[#7c4dff]/15 px-2 py-0.5 text-[10px] uppercase tracking-wider text-[#b39bff]">{r.category || "Sammler"}</div>
              <div className="mt-1 text-xs text-zinc-400">{r.year}</div>
              <div className="mt-1 text-sm font-semibold text-white">{r.name}</div>
              <p className="mt-1 text-xs text-zinc-500 line-clamp-2">{r.desc}</p>
            </div>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full text-center text-sm text-zinc-500 py-10">Keine Rares in dieser Kategorie.</div>
          )}
        </div>
      )}
    </section>
  );
};

export default Rares;
